import type { AnimationItem } from '@lumen5/lottie-web';
import lottie from '@lumen5/lottie-web';
import * as PIXI from 'pixi.js';
import type { CanvasSource } from 'pixi.js';
import type { AllProperties, LoadSettings } from '../../serializableMixin';
import { LOTTIE_ORIGINAL_DURATION } from '../../../constants';
import type { SeekMode } from '../../../enums';
import { BufferingMode, ValueMapMode } from '../../../enums';
import { BufferingAbortedError } from '../../../errors';
import { AnimationData } from '../../../libs/Lottie/AnimationData';
import { getPositionFromTransformation } from '../../../video-data/designs/scenes';
import type { ColorMapData } from '../../../libs/Lottie/plugins/ColorsPlugin';
import type { Lottie, Renderer } from '../../../libs/Lottie/types/lottie';
import { createValidator, retry } from '../../../libs/retry';
import type { Resolution } from '../../../types';
import { shouldLoadMedia } from '../../../types';
import {
    assertIsValidLottieDuration,
    assertIsValidUrl,
    assertMinimum,
    assertNumber,
    assertResponseOk,
} from '../../../validators';
import { Config } from '../../../video-data/config';
import {
    BooleanAttribute,
    getAllTemplateAttributes,
    getAttributesToSerialize,
    NumberAttribute,
    StringAttribute,
} from '../../attributes';
import type { BaseAssetSettings } from '../BaseAsset';
import { BaseAsset } from '../BaseAsset';
import { ColorMap } from '../ColorMap';
import { calculateScaledResolution } from '../mediaUtils';
import type { FillMode } from '../../../video-data/types';
import {
    getTemplateValueAttribute,
    objectHasTemplateValues,
    stripTemplateReferencePrefix,
} from '../../attributes/template/common';
import type { AudioAsset } from '../../../audio/AudioAsset';
import { DynamicStylingMixin } from '../../dynamicStylingMixin';
import { MorphManager } from '../../morph/MorphManager';
import { observable } from '../../reactivity';
import { cloneDeep } from '../../../libs/cloneDeep';
import { resetSprite } from '../utils';
import { TransformMatrix } from '../../../libs/TransformMatrix';
import { SwitchableContextProxy, createFakeContext } from './swtitchableContextProxy';
import { ErrorReporter } from '../../../libs/error-reporter';
import { isTrustedLottieSource } from '../../../libs/Lottie/lottieProvenance';
import { toCdnUrl } from '../../../libs/cdnUrl';
import {
    createRendererSandbox,
    ensureSharedExpressionSandbox,
    getSharedExpressionSandbox,
    reportSandboxLoadFailureOnce,
} from '../../../libs/Lottie/expressionSandbox';

export type LottieAssetDuration = number | typeof LOTTIE_ORIGINAL_DURATION;

export type LottieAssetSettings = BaseAssetSettings & {
    renderWhenInvisible?: boolean;
    enableCanvasLottie: boolean;
};

const PRECISION_TOLERANCE = 1e-3;

class TextureDestroyedError extends Error {
    override name = 'TextureDestroyedError';

    constructor() {
        super('TextureDestroyedError');
    }
}

/**
 * This asset class enables luminary to take advantage of [Lottie]{@link https://lottiefiles.com/}!
 * Given a sourceUrl, this asset will download the lottie file and then render it with the
 * lottie SVG renderer (refer to it's supported features
 * [here]{@link https://lottiefiles.com/supported-features}).
 *
 * Note there are some performance limitations of using the Lottie SVG renderer. The reason
 * being that it's relatively compute intensive to extract the SVG image into a texture which
 * PIXI can use. To help limit the complexity of lottie designs, this asset imposes a max file
 * size limit. If in the future we want to support more complex designs, perhaps with icons,
 * then we should also support using the canvas renderer. The canvas renderer is more limited in
 * it's supported features, but since it renders to a canvas a webGL texture can be created much
 * more efficiently.
 */
export class LottieAsset extends DynamicStylingMixin(BaseAsset) {
    override _classType: string = Config.LOTTIE_ASSET_TYPE;

    // Attributes

    /**
     * A url to the lottie json file.
     * Production json files should be uploaded to the gcloud bucket lumen5-prod-lottie.
     */
    #sourceUrl = 'https://storage.googleapis.com/lumen5-prod-lottie/lottie1.json';

    /**
     *  Defines how the asset should be sized to fit it's container.
     *  COVER - the asset will completely cover its container
     *  FIT - the asset will fully fit within its container as snugly as possible
     *  AREA - the area of the asset will match the area of the container
     */
    @StringAttribute({ choices: Config.ALL_FILL_MODES })
    accessor fillMode: FillMode = Config.FILL_COVER;

    /**
     *  Defines how the lottie asset should behave if the container duration exceeds
     *  the asset's duration. Once the lottie duration has been exceeded do one of:
     *  LOOP - start its animation over at the beginning (as many times as necessary)
     *  HOLD - keep showing the last frame for the remainder of the duration
     */
    @StringAttribute({ choices: Config.ALL_PLAYBACK_MODES })
    @observable
    accessor playbackMode = Config.PLAYBACK_HOLD;

    /**
     * Max width of visible area.
     */
    @NumberAttribute({ serialize: false })
    accessor maxWidth = 0;

    /**
     * Max height of visible area.
     */
    @NumberAttribute({ serialize: false })
    accessor maxHeight = 0;

    /**
     * Duration to fit the Lottie animation in.
     *
     * `LOTTIE_ASSET_DURATION` means to fit the Lottie animation in its original duration, i.e. no scaling will happen.
     * Ex. if animation is 1000ms long, we scale it to fit in 1000ms.
     *
     * If a duration is number, we scale the Lottie animation to fit in that many milliseconds.
     * Ex. if duration is 500 and animation is 1000ms, we scale it to fit in 500ms.
     */
    #duration: LottieAssetDuration = LOTTIE_ORIGINAL_DURATION;

    /**
     * Stores the required color mapping data of lottie asset.
     * Color mapping data includes the following:
     * - source color: color in lottie json
     * - target color: custom color or theme reference
     * - target paths: list of lottie json paths where to replace source color with target color
     * This is supposed to be an attribute in https://lumen5.atlassian.net/browse/LU-1943
     */
    #colorMaps: ColorMap[] = [];

    @BooleanAttribute()
    accessor flipHorizontally = false;
    @BooleanAttribute()
    accessor flipVertically = false;

    @StringAttribute({ choices: Config.ALL_LOTTIE_RENDERERS })
    accessor renderer: Renderer = Config.LOTTIE_RENDERER_CANVAS;

    // Shared variables

    #loadingSourceUrl: string | null = null;
    #loadedSourceUrl: string | null = null;

    #loadedLottieData: Lottie | null = null;

    #reportedExpressions = new Set<string>();

    #rendererSandbox: ReturnType<typeof createRendererSandbox> | null = null;

    #animationData: null | AnimationData = null;
    #animationItem: null | AnimationItem = null;

    #failed = false;
    #abortBuffering: (error: BufferingAbortedError) => void = () => {};

    /**
     * Common PIXI sprite shared by all renderers.
     */
    #sprite = new PIXI.Sprite();

    _useUnpremultipliedMaskTexture = false;

    /**
     * File size of fetched lottie asset.
     * This is used for assets using svg renderer.
     */
    #fileSize = 0;

    /**
     * Current lottie asset frame.
     * Note: This is only used in lottie_asset_test.js
     */
    _currentFrame = -1;

    /**
     * Temporary variable to save existing color maps during replace action.
     */
    _lastColorMaps: readonly ColorMap[] = [];

    /**
     * Value used to decide if a rebuild of the Lottie renderer is required.
     */
    #cacheSignature = '';

    // Canvas renderer variables
    #contextProxy: SwitchableContextProxy | null = null;
    #progress = 0;

    // SVG renderer variables

    /**
     * This is the number of frames in advance to compute a texture for during playback.
     * This is required because of the Lottie SVG renderer. Transforming the SVG into a texture
     * is an async process and can take 10s of milliseconds to complete. This is a somewhat
     * arbitrary threshold and was determined by trial and error. Increasing this means we would
     * support more complicated lottie files (because we increase the time from when the
     * creation of the texture begins to when the texture is needed), but the trade off is more
     * memory consumption and longer seek times when seeking for playback.
     */
    static SVG_NUM_FRAMES_TO_PRECOMPUTE = 10;

    /**
     * This stores the pre-generated textures for a specified frame index. Note that this
     * implementation intentionally does not use the PIXI Texture Cache. The reason is that each
     * LottieAsset is managing its own textures, and notably destroying them. If we wanted to
     * share textures between LottieAssets then we'd have to track the usage of the textures
     * before cleaning them up. Also, since we only keep the current and next few textures
     * around, it's unlikely that we'd be able to re-use a lot of textures since only one (or
     * maybe a handful) of LottieAssets will be playing back at a given time.
     */
    #svgResourceCache = new Map<number, HTMLImageElement>();

    /**
     * The container used for static scaling and rotating.
     */
    #scaleRotateContainer = new PIXI.Container();

    // TransformMatrix extends mixins that don't expose constructor params properly in TS
    #transformMatrix: TransformMatrix = new (TransformMatrix as unknown as new (settings: {
        parent: LottieAsset;
    }) => TransformMatrix)({
        parent: this,
    });

    /**
     * Allows rendering of Lottie asset when invisible.
     */
    #renderWhenInvisible = false;

    /**
     * When true, forces the canvas renderer regardless of the `renderer` attribute.
     */
    #enableCanvasLottie: boolean;

    _waveformAudioAsset: AudioAsset | undefined = undefined;

    constructor(container: BaseAsset, settings: LottieAssetSettings) {
        super(container, settings);
        this.#renderWhenInvisible = !!settings.renderWhenInvisible;
        this.#enableCanvasLottie = settings.enableCanvasLottie;
        const referenceResolution = this.getReferenceResolution();
        this.maxWidth = referenceResolution.width;
        this.maxHeight = referenceResolution.height;
        this.#scaleRotateContainer.label = 'scaleRotateContainer';
        this._addPixiChild(this.#scaleRotateContainer);
        this.#scaleRotateContainer.addChild(this.#sprite);
    }

    get sprite() {
        return this.#sprite;
    }

    get animationData() {
        return this.#animationData;
    }

    get isAssetVisible() {
        return this.visible && this.container.visible;
    }

    get isRenderAllowed() {
        return this.visible || this.#renderWhenInvisible;
    }

    get duration(): number {
        if (this.#duration !== LOTTIE_ORIGINAL_DURATION) {
            return this.#duration;
        }
        if (this.animationData) {
            return this.animationData.originalDuration;
        }
        return 0;
    }

    set duration(value: LottieAssetDuration) {
        if (this.#duration === value) {
            return;
        }
        assertIsValidLottieDuration(value);
        this.#duration = value;
        this.requiresRedraw = true;
    }

    get colorMaps(): readonly ColorMap[] {
        return this.#colorMaps;
    }

    getNonTransparentColorMap(): ColorMap[] {
        if (!this.#animationData) {
            return [];
        }
        const nonTransparentSourceColors = new Set(
            this.#animationData.getNonTransparentColorMaps().map((cm) => cm.sourceColor),
        );
        return this.#colorMaps.filter((colorMap) =>
            nonTransparentSourceColors.has(colorMap.sourceColor),
        );
    }

    get fileSize() {
        return this.#fileSize;
    }

    get sourceUrl(): string {
        return this.#sourceUrl;
    }

    set sourceUrl(value: string) {
        if (this.#sourceUrl === value) {
            return;
        }
        assertIsValidUrl(value);
        this.#resetAsset();
        this.#sourceUrl = value;
        this.requiresRedraw = true;
    }

    override isMorphMatch(other: BaseAsset): boolean {
        const otherLottie = other as LottieAsset;
        if (!Config.isLottieAsset(otherLottie)) {
            return false;
        }

        if (
            this.playbackMode === Config.PLAYBACK_WAVEFORM ||
            otherLottie.playbackMode === Config.PLAYBACK_WAVEFORM
        ) {
            return false;
        }

        if (this.sourceUrl !== otherLottie.sourceUrl) {
            return false;
        }

        return true;
    }

    get lottieData(): Lottie {
        if (!this.#animationData) {
            throw new Error('Cannot get lottie json, no animationData available');
        }
        return this.#animationData.originalValue;
    }

    set lottieData(value: Lottie) {
        if (this.#animationData && this.#animationData.isOriginalValueEqualTo(value)) {
            return;
        }
        this.#resetAsset();
        this.#animationData = this._createAnimationData(value);
        this.requiresRedraw = true;
    }

    get playbackSpeed() {
        if (!this.animationData) {
            throw new Error('Cannot get playback speed, no animationData available');
        }
        return this.animationData.playbackSpeed;
    }

    set playbackSpeed(value: number) {
        if (!this.animationData) {
            throw new Error('Cannot set playback speed, no animationData available');
        }
        if (this.animationData.playbackSpeed === value) {
            return;
        }
        assertNumber(value, false);
        assertMinimum(value, 0);
        this.animationData.updatePlaybackSpeed(value);
        this.#duration = this.#animationData!.duration;
        this.requiresRedraw = true;
    }

    get useSvgRenderer() {
        return this.#enableCanvasLottie ? false : this.renderer === Config.LOTTIE_RENDERER_SVG;
    }

    get useCanvasRenderer() {
        return this.#enableCanvasLottie ? true : this.renderer === Config.LOTTIE_RENDERER_CANVAS;
    }

    /**
     * Determines if the animation data from the sourceUrl has been loaded into the lottie
     * renderer and the renderer is ready.
     */
    get isLottieRendererLoaded(): boolean {
        if (!this.#animationData) {
            return false;
        }
        if (!this.#animationItem) {
            return false;
        }
        return this.#animationItem.isLoaded;
    }

    /**
     * Determines if the animationData from the sourceUrl has been fetched successfully
     */
    get isSourceUrlLoaded(): boolean {
        return this.sourceUrl === this.#loadedSourceUrl;
    }

    /**
     * Indicates if the current sourceUrl is in the process of loading
     */
    get isSourceUrlLoading(): boolean {
        if (this.#loadingSourceUrl === null) {
            return false;
        }
        return this.#loadingSourceUrl === this.sourceUrl;
    }

    /**
     * Step-by-step description of the build process
     *
     * Load source
     * - Fetch lottie json if lottie json is null and source url is not null
     *
     * Fit lottie animation in duration
     * - Update playback speed such that entire animation completes in given duration.
     *
     * Initialize color maps and renderer
     * - Initialize them only if color maps are empty.
     * - Color maps will be empty when `sourceUrl` is changed via the editor because we force clear them.
     * - We need to initialize renderer because new source url may need a different renderer.
     *
     * Remap colors
     * - If we have saved last color maps, we should remap them as per requirement.
     * - This happens when we replace lottie asset (currently, ony icons can be replaced).
     * - The old color maps have correct `targetColor` (set by designer) and new color maps have correct `targetPaths` (from new lottie json).
     * - In order for correct colors to apply to replace icon, we need to copy `targetColor` from old color map to new color map.
     * - Note: new color maps are already initialized at this point because of step 4.
     *
     * Replace colors
     * - We now have correct color maps for new lottie asset, but lottie json still has original colors.
     * - this is why we have to replace original colors in lottie json with new colors.
     * - New colors can be template refs or custom color value.
     *
     * Build lottie animation and resources
     * - If previously we used canvas renderer, we need to destroy it because it is possible we may need to use SVG renderer this time.
     * - If previously we used svg renderer, we need to destroy it because it is possible we may need to use canvas renderer this time.
     * - Initialize lottie renderer with color mapped lottie json and ensure PIXI can render lottie.
     * - Also create required PIXI resources required for rendering lottie animation
     *
     * Update dynamic styles
     * - We destroy PIXI sprite/texture in step 1 of build because we are not sure what kind of renderer we would need in this iteration.
     * - Dynamic styles, especially color graphics require the sprite/texture to be loaded before we can calculate its bounds.
     * - We need to wait until lottie asset is loaded and the sprite/texture is valid before we can update dynamic styles.
     *
     * Intro/outro animations
     * - These are inherited from base asset into lottie asset and hence need to be updated when lottie asset is built.
     * - These animations are applied on the scene level which is never destroyed.
     */
    override _build() {
        super._build();

        this.#fetchSourceData();

        // fit lottie animation is given duration
        this._fitLottieInDuration();

        // initialize color maps and renderer if not already done
        if (this.colorMaps.length === 0) {
            this.#initializeColorMaps();
        }

        // remap old and new color maps if required
        if (this._lastColorMaps.length > 0) {
            this._remapColors();
        }

        // replace colors in lottie json
        this.#replaceColors();

        // build animation and pixi resources using new color mapped lottie json
        this.#buildLottieRenderer();

        // base asset
        this.clearAnimation();
        this.resetTransformation();
        if (this.#animationData) {
            const { width, height } = this.#getSpriteSize();
            this.#sprite.width = width;
            this.#sprite.height = height;
        }
        this.#ensureSpriteHasValidTexture();
        if (this.isLoaded) {
            this._prepareDynamicStylingForUpdate();

            this.#applyPosition();
            this._applyAnimation();
            this._updateDynamicStyling();
            this.#applyFlip();
            this.#applyRotation();
        }

        this._applyIntroAnimations();
        this._applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    #fetchSourceData() {
        // fetch lottie json if not already fetched
        if (!this.#animationData && this.sourceUrl) {
            this.#loadSourceWithNetworkManager();
        }
    }

    #loadSourceWithNetworkManager() {
        if (!this._shouldLoad) {
            return;
        }
        if (this.isSourceUrlLoading || this.isSourceUrlLoaded) {
            return;
        }

        this.#failed = false;
        this.sentCanPlay = false;

        const url = this.sourceUrl;
        this.#loadingSourceUrl = url;
        this.#loadedSourceUrl = null;
        this.#fileSize = 0;
        this.#animationData = null;

        this._assetNetworkManager.waitForNetworkSlot(this, (requestFinished) => {
            this.#loadSource(url)
                .finally(() => {
                    requestFinished();
                    this.requiresRedraw = true;
                })
                .catch((e) => {
                    if (url !== this.#loadingSourceUrl) {
                        return; // another fetch has started so ignore the result of this one
                    }
                    console.error(`LottieAsset failed to load source: ${url}, ${e}`);
                    this.#markAsFailed(e);
                });
        });
    }

    async #loadSource(sourceUrl: string) {
        // Started alongside the fetch rather than after it: the build loop that needs the
        // interpreter is synchronous, so whatever is not ready by then renders with
        // expressions off. The fetch is almost always the slower of the two, so waiting on
        // this costs nothing. A sandbox that fails to load must not fail the asset -
        // #expressionRendererSettings still has its fallback.
        const sandboxReady = isTrustedLottieSource(sourceUrl)
            ? Promise.resolve()
            : ensureSharedExpressionSandbox().then(
                  () => undefined,
                  (error: unknown) => {
                      reportSandboxLoadFailureOnce(() => {
                          ErrorReporter.capture(error, { extra: { sourceUrl } });
                      });
                  },
              );
        const { json, size } = await this.#doFetch(sourceUrl);
        if (sourceUrl !== this.#loadingSourceUrl) {
            return; // another fetch has started so ignore the result of this one
        }
        await sandboxReady;
        if (sourceUrl !== this.#loadingSourceUrl) {
            return;
        }
        this.#fileSize = size;
        this.#animationData = this._createAnimationData(json);
        this.#loadingSourceUrl = null;
        this.#loadedSourceUrl = sourceUrl;
    }

    async #doFetch(sourceUrl: string): Promise<{ json: Lottie; size: number }> {
        const validator = createValidator(assertResponseOk);
        const maxAttempts = this._rootOrNull?.settings.loadStatusMaxCount || 3;
        const response = await retry(maxAttempts, validator(fetch), toCdnUrl(sourceUrl));
        const blob = await response.blob();

        const type = blob.type;
        const isJson = type.includes('application/json');
        const isText = type.includes('text/plain');
        if (!(isJson || isText)) {
            throw new Error(`Invalid sourceUrl for lottie asset, detected type: ${type}`);
        }

        const size = blob.size;
        const text = await blob.text();
        const json = JSON.parse(text) as Lottie;
        return { json, size };
    }

    /**
     * Marks the asset as failed to load and emits assetCanPlay. Note this should only be called
     * when a failure happens during load and build but not during animate.
     */
    #markAsFailed(error: Error) {
        this.#failed = true;
        this.emit('loadFailed', error);
        this._emitAssetCanPlay();
    }

    _createAnimationData(json: Lottie) {
        return new AnimationData(json);
    }

    #initializeColorMaps() {
        if (!this.#animationData) {
            return;
        }
        const colorMapObjects = this.#animationData.colorMaps;
        this._colorMapsCustomLoad(colorMapObjects, { skipTemplateAttributes: false });
    }

    _remapColors() {
        if (!this.#animationData) {
            return;
        }
        // make sure to clear last color maps
        // to skip remapping in next iteration of build loop
        this._lastColorMaps = [];
    }

    #replaceColors() {
        if (!this.#animationData) {
            return;
        }
        this.#animationData.replaceColors(this.#colorMaps);
    }

    _fitLottieInDuration() {
        if (!this.animationData) {
            return;
        }
        const duration = this.duration;
        // We can't do a simple value comparison here since adjust the duration by scaling the playback speed
        // which can result in precision errors. We use a tolerance value to account for this.
        if (Math.abs(this.animationData.duration - duration) < PRECISION_TOLERANCE) {
            return;
        }
        this.animationData.updateDuration(duration);
    }

    get animationItem(): AnimationItem | null {
        return this.#animationItem;
    }

    #buildLottieRenderer() {
        if (!this._shouldLoad) {
            return;
        }
        if (!this.#animationData) {
            return;
        }
        if (!this.#isRebuildRequired()) {
            return;
        }
        this.#destroyResources();
        this.#buildAnimationItem();
    }

    #isRebuildRequired() {
        const cacheSignature = this.#createCacheSignature();
        const isCacheSignatureEqual = cacheSignature === this.#cacheSignature;
        const isLoadedLottieDataEqual = this.#animationData!.isCurrentValueEqualTo(
            this.#loadedLottieData as Lottie,
        );
        this.#cacheSignature = cacheSignature;
        return !(isCacheSignatureEqual && isLoadedLottieDataEqual);
    }

    #createCacheSignature() {
        const attributes = getAttributesToSerialize(this).concat('maxWidth', 'maxHeight');
        const data = this._savePropertyList(attributes, { returnTemplateReference: false });
        return JSON.stringify(data);
    }

    #destroyResources() {
        this.#destroySvgTextureCache();
        if (this.#animationItem) {
            this.#animationItem.destroy();
            this.#animationItem = null;
        }
        // The player compiled these and has no way to release them, so release them here.
        // A rebuild runs on every attribute change and recompiles the same expressions.
        this.#rendererSandbox?.disposeCompiled();
        this.#rendererSandbox = null;
        this.#contextProxy = null;
    }

    /**
     * Clears the texture cache and destroys each texture within it.
     */
    #destroySvgTextureCache() {
        this.#svgResourceCache.forEach((_img, frame) => {
            this.#destroyTextureAtFrame(frame);
        });
        this.#svgResourceCache.clear();
    }

    #destroyTextureAtFrame(frame: number) {
        const img = this.#svgResourceCache.get(frame);
        if (!img) {
            return;
        }

        img.removeAttribute('src');

        this.#svgResourceCache.delete(frame);
    }

    #getTextureSize() {
        const { width, height } = this.#getSpriteSize();

        const sharedTextureResolution = this.getOutputResolution();
        return this.#getResolutionToFitContainer(sharedTextureResolution, {
            width,
            height,
        });
    }

    /** Designer-published lottie keeps lottie-web's evaluator; anything else is sandboxed. */
    #expressionRendererSettings(): {
        expressionSandbox?: ReturnType<typeof createRendererSandbox>;
        runExpressions?: boolean;
    } {
        if (isTrustedLottieSource(this.#loadedSourceUrl)) {
            return {};
        }
        const sandbox = getSharedExpressionSandbox();
        if (!sandbox) {
            // #loadSource waits for the interpreter, so reaching here means it failed to
            // load or was discarded after exhausting its memory. Render this pass with
            // expressions off rather than letting one reach eval, and rebuild once it is
            // back - clearing the signature is what makes #isRebuildRequired agree, since
            // neither the attributes nor the lottie data have changed.
            ensureSharedExpressionSandbox()
                .then(() => {
                    if (!this.destroyed) {
                        this.#cacheSignature = '';
                        this.requiresRedraw = true;
                    }
                })
                .catch((error) => {
                    reportSandboxLoadFailureOnce(() => {
                        ErrorReporter.capture(error, {
                            extra: { sourceUrl: this.#loadedSourceUrl },
                        });
                    });
                });
            return { runExpressions: false };
        }
        this.#rendererSandbox = createRendererSandbox(sandbox, (source, error) => {
            // lottie-web latches per property; this collapses repeats across them.
            if (this.#reportedExpressions.has(source)) {
                return;
            }
            this.#reportedExpressions.add(source);
            ErrorReporter.capture(error, {
                extra: { sourceUrl: this.#loadedSourceUrl, expression: source },
            });
        });
        return { expressionSandbox: this.#rendererSandbox };
    }

    #buildAnimationItem() {
        if (this.useSvgRenderer) {
            const container = document.createElement('div');
            const { width, height } = this.#getTextureSize();

            /**
             * Lottie asset inherits its dimension from the container.
             * This container needs to be appended to the DOM during load.
             * The css ensures that it does not cause any kind of flicker, flash of lottie asset
             * or any random scrollbars due to appending of a large div in the DOM.
             */
            container.style.cssText = `position:fixed;width:${width}px;height:${height}px;right:150vw;bottom:150vh;opacity:0;`;

            /**
             * Lottie uses offsetWidth and offsetHeight to calculate the dimensions of canvas
             * But offsetWidth and offsetHeight trigger a reflow.
             * We don't need it because we already know the dimensions.
             */
            Object.defineProperty(container, 'offsetWidth', {
                get() {
                    return width;
                },
            });

            Object.defineProperty(container, 'offsetHeight', {
                get() {
                    return height;
                },
            });
            this.#animationItem = lottie.loadAnimation<'svg'>({
                container,
                loop: false,
                autoplay: false,
                animationData: cloneDeep(this.#animationData!.value),
                renderer: 'svg',
                rendererSettings: this.#expressionRendererSettings(),
            });
        } else {
            // We want to avoid canvas creation, because we use shared textures,
            // but when we initialize we need to provide a context to lottie.
            // unfortunately, under the hood lottie creates a canvas for masks when it creates animation, lottie-web does not allow us switch context
            // we need to provide lottie information about maximum expected canvas size.

            const { width: sharedTextureWidth, height: sharedTextureHeight } =
                this.#getTextureSize();

            this.#contextProxy = new SwitchableContextProxy(
                createFakeContext(sharedTextureWidth, sharedTextureHeight),
            );
            this.#animationItem = lottie.loadAnimation<'canvas'>({
                container: null as unknown as Element,
                loop: false,
                autoplay: false,
                animationData: cloneDeep(this.#animationData!.value),
                renderer: 'canvas',
                rendererSettings: {
                    context: this.#contextProxy.getProxy(),
                    clearCanvas: false,
                    dpr: 1,
                    ...this.#expressionRendererSettings(),
                },
            });
            this.#animationItem.isLoaded = true;
        }

        // Since we use animationData (instead of path), animationItem.isLoaded will be immediately true
        if (this.#animationItem.isLoaded) {
            this.#loadedLottieData = this.#animationData!.value;
            this.emit('lottieRendererLoaded');
        } else {
            this.#animationItem.addEventListener('data_ready', () => {
                this.#loadedLottieData = this.#animationData!.value;
                this.emit('lottieRendererLoaded');
            });
        }
        this.#animationItem.addEventListener('data_failed', (e) => {
            this.#loadedLottieData = null;
            console.error(`LottieAsset failed to load animation: ${e}`);
            this.#markAsFailed(e as unknown as Error);
        });
        this._emitAssetCanPlay();
    }

    /**
     * Returns the visible resolution in which the asset should fit depending on fillMode.
     */
    getVisibleResolution(): Resolution {
        // Deliberately mask-unaware: a mask's window is read through
        // approximateMaxBounds instead
        return { width: this.maxWidth, height: this.maxHeight };
    }

    /**
     * Typically seeking will drive the creation of textures. However, to ensure we
     * have something when the LottieAsset first gets created, this ensures the sprite will
     * receive a valid texture as part of build. That way once isLoaded resolves, there
     * will be an underlying PIXI sprite that has dimensions and is visible.
     */
    #ensureSpriteHasValidTexture() {
        if (!this._shouldLoad || this.isDoneTryingToLoad) {
            this._emitAssetCanPlay();
            return;
        }
        if (!this.isLottieRendererLoaded) {
            return;
        }
        if (!this.isRenderAllowed) {
            return;
        }
        if (this.useCanvasRenderer) {
            return;
        }
        this.#ensureResourceForFrame(this.#animationData!.firstFrameIndex)
            .then(() => {
                this.requiresRedraw = true;
            })
            .catch((e) => {
                this.#markAsFailed(e);
                console.error(`failed to create texture for initial sprite: ${e}`);
            });
    }

    get #originalResourceResolution(): Resolution {
        if (!this.#animationData) {
            throw new Error('Cannot get lottie resolution, no animationData available');
        }
        return { width: this.#animationData.width, height: this.#animationData.height };
    }

    #getResolutionToFitContainer(
        container: { width: number; height: number },
        object: { width: number; height: number },
    ) {
        const aspectRatio = object.width / object.height;
        const width = container.width;
        const height = width / aspectRatio;

        if (height > container.height) {
            return { height: container.height, width: container.height * aspectRatio };
        }
        return { width, height };
    }

    #getSpriteSize() {
        const { width, height } = calculateScaledResolution(
            this.#originalResourceResolution,
            {
                width: this.maxWidth,
                height: this.maxHeight,
            },
            this.fillMode,
        );
        return {
            width: width * this.scaleX,
            height: height * this.scaleY,
        };
    }

    override updateForRender() {
        super.updateForRender();
        if (!this.#animationItem || !this.#animationItem.isLoaded) {
            return;
        }

        this.#sprite.texture = this._reusableTextures.allocateTexture(() => {
            resetSprite(this.#sprite);
        });

        const sprite = this.#sprite;
        const sharedTexture = this.#sprite.texture;

        const { width: spriteWidth, height: spriteHeight } = this.#getSpriteSize();
        sprite.width = spriteWidth;
        sprite.height = spriteHeight;
        sprite.scale.x = 1;
        sprite.scale.y = 1;
        const originalMediaResolution = this.#getTextureSize();
        const scaled = calculateScaledResolution(
            originalMediaResolution,
            { width: sharedTexture.width, height: sharedTexture.height },
            this.fillMode,
        );
        const scaledMediaTextureResolution = this.#getResolutionToFitContainer(
            { width: sharedTexture.width, height: sharedTexture.height },
            scaled,
        );
        const texW = scaledMediaTextureResolution.width;
        const texH = scaledMediaTextureResolution.height;
        sharedTexture.frame.x = 0;
        sharedTexture.frame.y = 0;
        sharedTexture.frame.width = texW;
        sharedTexture.frame.height = texH;
        sharedTexture.orig.x = 0;
        sharedTexture.orig.y = 0;
        sharedTexture.orig.width = texW;
        sharedTexture.orig.height = texH;
        if (sharedTexture.trim) {
            sharedTexture.trim.x = 0;
            sharedTexture.trim.y = 0;
            sharedTexture.trim.width = texW;
            sharedTexture.trim.height = texH;
        }

        const canvas = (sharedTexture.source as CanvasSource).resource as HTMLCanvasElement;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error(
                'Failed to get 2D context from canvas; the canvas has already been set to a different context mode',
            );
        }
        ctx.imageSmoothingQuality = 'high';

        sprite.scale.x = spriteWidth / texW;
        sprite.scale.y = spriteHeight / texH;

        const currentFrame = this.#getCurrentFrame();

        ctx.resetTransform();
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (this.useCanvasRenderer) {
            this.#contextProxy!.switchContext(ctx);
            const nativeRes = this.#originalResourceResolution;
            ctx.scale(
                originalMediaResolution.width / nativeRes.width,
                originalMediaResolution.height / nativeRes.height,
            );
            this.#animationItem.goToAndStop(currentFrame, true);
        } else {
            const img = this.#svgResourceCache.get(currentFrame);
            if (!img || !img.complete) {
                // It's possible that the texture was destroyed before it could be created. In that case, we just skip drawing for this frame and wait for the next updateForRender to create a new texture.
                // or we didn't have anough time to create texture for current frame because of async nature of svg texture creation. In that case, we just skip drawing for this frame and wait for the next updateForRender to create a new texture.
                console.warn(`Texture for frame ${currentFrame} is not available to draw`);
                return;
            }
            ctx.drawImage(img, 0, 0);
        }

        if (this._useUnpremultipliedMaskTexture) {
            this.#sprite.texture.source.alphaMode = 'no-premultiply-alpha';
        }
        this.#sprite.texture.update();
        this.#sprite.texture.source.update();
        this.updateTransform();
    }

    protected override _getMatteSourceSprite(): PIXI.Sprite | null {
        return this.#sprite ?? null;
    }

    #getCurrentFrame() {
        const progress = this.#progress;
        if (
            this.playbackMode !== Config.PLAYBACK_WAVEFORM ||
            !this._waveformAudioAsset ||
            !this.#animationData
        ) {
            return this.#getFrameAtProgress(progress);
        }

        const currentTime = this._waveformAudioAsset._getAssetTimeFromPlaybackTime(
            this.luminaryParent!.currentTime + this.slide!.startTimeRelativeToSceneGroup,
        );

        const smoothedLoudness = this._waveformAudioAsset.getLoudnessAndPeakMixAtTime(
            currentTime,
            0.95,
            3.5,
            100,
        );

        const clampedLoudness = Math.max(0, Math.min(1, smoothedLoudness));
        const targetTimeSeconds = (this.duration * clampedLoudness) / 1000;
        return this.#animationData.getFrameAtTime(targetTimeSeconds, this.playbackMode);
    }

    override _seek(progress: number, seekMode: SeekMode) {
        if (this.destroyed) {
            return;
        }
        super._seek(progress, seekMode);

        if (!this.isRenderAllowed) {
            return;
        }
        this.#progress = progress;
        const currentFrame = this.#getCurrentFrame();

        if (!this.#animationItem || !this.#animationItem.isLoaded) {
            return;
        }

        if (this.useSvgRenderer) {
            const resource = this.#svgResourceCache.get(currentFrame);
            if (!resource || !resource.complete) {
                this.#ensureResourceForFrame(currentFrame).catch((e) => {
                    console.error(`failed to load texture for frame: ${currentFrame}: ${e}`);
                });
            } else {
                for (const cachedFrame of this.#svgResourceCache.keys()) {
                    if (cachedFrame === currentFrame) {
                        break;
                    }
                    this.#destroyTextureAtFrame(cachedFrame);
                }
            }

            // while playing, generate extra frames for better performance of svg renderer
            if (this.luminaryParent!.isPlaying) {
                this.#ensureResourcesForNextFrames(currentFrame).catch((e) => {
                    console.error(`failed to load texture for next frame: ${e}`);
                });
            }
        }
    }

    /**
     * Pre-generate some textures for the LottieAsset. resolveWhenSingleFrame will
     * only ensure the current frame has a texture, while resolveWhenCanPlay will ensure the
     * current and next several frames (defined by NUM_FRAMES_TO_PRECOMPUTE) have textures.
     */
    override async _bufferAtTime(
        time: number,
        bufferingMode = BufferingMode.ResolveWhenSingleFrame,
    ): Promise<void> {
        if (!this._shouldLoad) {
            return;
        }
        if (this.failedToLoad) {
            return;
        }
        if (!this.isRenderAllowed) {
            return;
        }

        // Using PrepareForPlayback causes a race condition in LottieAsset (see LU-2255).
        // Since it's not required for LottieAsset, we are skipping this mode.
        // (Unlike MediaAssets, LottieAsset do not need to be seeked to 0 when replaying)
        if (bufferingMode === BufferingMode.PrepareForPlayback) {
            return;
        }

        try {
            await this.onLoaded();
            await this.#bufferAtTime(time, bufferingMode);
        } catch (e) {
            if (e instanceof BufferingAbortedError) {
                // Don't propagate an error, but rather just resolve
                return;
            }
            throw e;
        }
    }

    async #bufferAtTime(time: number, bufferingMode: BufferingMode): Promise<void> {
        const progress = time / this.container.duration;
        this.#progress = progress;
        await this.#ensureAudioBufferIfWaveform();
        // Buffer whatever frame `#draw` will ask for. `#getCurrentFrame` picks one selector or the
        // other, never both: the progress frame for an ordinary lottie, the loudness-derived frame
        // for a waveform. Buffering the progress frame unconditionally left a waveform draw without
        // a texture, and the asset only rendered at all because `_seek` separately fires an
        // un-awaited request for the right frame that usually, but not always, lands first.
        //
        // Both preceding lines are load-bearing for the waveform branch: it needs the decoded audio
        // for the loudness, and it reads the root's `currentTime`, which `Luminary.#seek` assigns
        // before it awaits buffering.
        const frame = this.#getCurrentFrame();
        if (this.useSvgRenderer) {
            try {
                switch (bufferingMode) {
                    case BufferingMode.ResolveWhenSingleFrame:
                        await this.#ensureResourceForFrame(frame);
                        break;
                    case BufferingMode.ResolveWhenCanPlay:
                        await Promise.all([
                            this.#ensureResourceForFrame(frame),
                            this.#ensureResourcesForNextFrames(frame),
                        ]);
                        break;
                    default:
                        throw new Error(`Unrecognized seekMode of ${bufferingMode}`);
                }
            } catch (e) {
                if (e instanceof BufferingAbortedError) {
                    // Don't propagate an error, but rather just resolve
                    return;
                }
                if (e instanceof TextureDestroyedError) {
                    // Don't propagate an error, but rather just resolve
                    // because the texture was destroyed before it could be created.
                    // The next `requiresRedraw` will take care of it.
                    return;
                }
                throw e;
            }
        }
        this._currentFrame = frame;
    }

    /**
     * @internal
     * Usually, this.#animationItem.goToAndStop is called in updateForRender,
     * but for push transitions lottie asset used just for holding animationItem,
     * we don't need to draw anything on screen, we just need to calculate correct transformation matrix using lottie.
     */
    _seekAnimationItem(progress: number) {
        if (!this.#animationItem || !this.#animationItem.isLoaded) {
            return;
        }
        this.#progress = progress;
        const currentFrame = this.#getCurrentFrame();
        this.#animationItem.goToAndStop(currentFrame, true);
    }

    #getFrameAtProgress(containerProgress: number): number {
        if (!this.#animationItem) {
            return -1;
        }
        if (!this.#animationData) {
            return -1;
        }
        const morphOffsetMS = MorphManager.getMediaTimeOffset(this) || 0;
        const currentTimeSeconds =
            (this.container.duration * containerProgress + morphOffsetMS) / 1000;
        return this.#animationData.getFrameAtTime(currentTimeSeconds, this.playbackMode);
    }

    async #ensureResourcesForNextFrames(frame: number): Promise<Array<void>> {
        if (!this.#animationItem || !this.#animationData) {
            return [];
        }
        const promises: Array<Promise<void>> = [];
        for (let i = 1; i <= LottieAsset.SVG_NUM_FRAMES_TO_PRECOMPUTE; i++) {
            const nextFrame = Math.max(
                (frame + i) % (this.#animationData.lastFrameIndex + 1),
                this.#animationData.firstFrameIndex,
            );
            promises.push(this.#ensureResourceForFrame(nextFrame));
        }
        return Promise.all(promises);
    }

    async #ensureResourceForFrame(frame: number): Promise<void> {
        if (
            !this.#animationItem ||
            !this.#animationData ||
            frame < this.#animationData.firstFrameIndex ||
            frame > this.#animationData.lastFrameIndex
        ) {
            return;
        }

        if (this.#svgResourceCache.has(frame)) {
            const img = this.#svgResourceCache.get(frame)!;
            if (!img.complete) {
                return new Promise<void>((resolve, reject) => {
                    img.addEventListener('load', () => resolve(), { once: true });
                    img.addEventListener('error', reject, { once: true });
                    this.#abortBuffering = reject;
                });
            }
            return;
        }

        this.#animationItem.goToAndStop(frame, true);
        const svg = this.#animationItem.renderer.svgElement;

        // ensure the proper dimensions are set before rasterizing
        const { width, height } = this.#getTextureSize();
        svg.setAttribute('width', width);
        svg.setAttribute('height', height);
        const serializedSvg = new XMLSerializer().serializeToString(svg);

        const img = new Image();
        img.width = width;
        img.height = height;
        this.#svgResourceCache.set(frame, img);

        await new Promise<void>((resolve, reject) => {
            this.#abortBuffering = () => {
                reject(
                    new BufferingAbortedError(
                        this.classType,
                        this.slide?.index,
                        `seek aborted while creating texture for frame ${frame}`,
                    ),
                );
            };
            img.onload = () => resolve();
            img.onerror = reject;
            img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(serializedSvg)}`;
        });
    }

    async #ensureAudioBufferIfWaveform(): Promise<void> {
        if (this.playbackMode !== Config.PLAYBACK_WAVEFORM || !this._waveformAudioAsset) {
            return;
        }
        await this._waveformAudioAsset.waitForAudioBuffer();
    }

    /**
     * Resolves once the data from the sourceUrl has been fetched and validated
     */
    async onLoaded(): Promise<void> {
        if (this.isLottieRendererLoaded) {
            return;
        }
        return new Promise<void>((resolve, reject) => {
            this.once('lottieRendererLoaded', resolve);
            this.once('loadFailed', reject);
            this.#abortBuffering = reject;
        });
    }

    override get _shouldLoad(): boolean {
        return shouldLoadMedia(this.fetchResources);
    }

    override get failedToLoad(): boolean {
        return super.failedToLoad || this.#failed;
    }

    /**
     * Reports loaded once the lottie renderer is loaded and the underlying Sprite has
     * a valid texture.
     */
    override get isLoaded(): boolean {
        if (!super.isLoaded) {
            return false;
        }
        if (!this._shouldLoad) {
            return true;
        }
        if (!this.isLottieRendererLoaded) {
            return false;
        }
        if (!this.isRenderAllowed) {
            return true;
        }

        if (this.useSvgRenderer) {
            return this.#firstFrameInSvgCacheIsValid;
        }
        return true;
    }

    get #firstFrameInSvgCacheIsValid() {
        const [firstFrame] = [...this.#svgResourceCache.values()];
        if (!firstFrame) {
            return false;
        }
        return firstFrame.complete;
    }

    override buildTemplateReferenceMap() {
        const result = super.buildTemplateReferenceMap();
        this.colorMaps.forEach((colorMap, colorMapIndex) => {
            const attributeKeyPrefix = `colorMaps.${colorMapIndex}`;
            const attributes = getAllTemplateAttributes(
                colorMap,
                ValueMapMode.GET_REFERENCE_VALUES,
            );
            attributes.forEach((attribute) => {
                const attributeKey = `${attributeKeyPrefix}.targetColor`;
                result[attributeKey] = stripTemplateReferencePrefix(
                    getTemplateValueAttribute(colorMap, attribute) as string,
                );
            });
        });
        return result;
    }

    _sourceUrlCustomLoad(value: string, _settings: LoadSettings) {
        this.#sourceUrl = value;
    }

    /**
     * For documentation, see serializableMixin.save
     */
    _colorMapsCustomSave(
        allProperties: AllProperties,
        clone: boolean,
        returnTemplateReference: boolean,
        skipTemplateAttributes: boolean,
    ) {
        return this.#colorMaps.map((colorMap) =>
            colorMap.save(allProperties, clone, returnTemplateReference, skipTemplateAttributes),
        );
    }

    /**
     * For documentation, see serializableMixin.load
     */
    _colorMapsCustomLoad(values: ColorMapData[], settings: LoadSettings) {
        if (!values) {
            return;
        }
        this.#colorMaps = values.map((value) => {
            const colorMap = new ColorMap({ parent: this });
            colorMap.load(value, settings);
            return colorMap;
        });
    }

    override _hasTemplateReferenceValues(): boolean {
        if (!this.colorMaps) {
            return false;
        }
        return this.colorMaps.some((colorMap) => objectHasTemplateValues(colorMap));
    }

    #resetAsset() {
        this.#sourceUrl = null as unknown as string;
        this.#loadingSourceUrl = null;
        this.#loadedSourceUrl = null;
        this.#fileSize = 0;
        this.#colorMaps = [];
        this.#duration = LOTTIE_ORIGINAL_DURATION;
        this.#loadedLottieData = null;
        this.#animationData = null;
    }

    override destroy() {
        this.#abortBuffering(
            new BufferingAbortedError(this.classType, this.slide?.index, 'destroy called'),
        );
        super.destroy({ children: true });
        this.#destroySvgTextureCache();
        if (this.#animationItem) {
            this.#animationItem.destroy();
            this.#animationItem = null;
        }
        this.#rendererSandbox?.disposeCompiled();
        this.#rendererSandbox = null;
        this.#loadedSourceUrl = null;
        this.#loadingSourceUrl = null;
        this.#loadedLottieData = null;
        this.#animationData = null;
        this.#sprite = null as unknown as PIXI.Sprite;
    }

    get transformMatrix(): TransformMatrix {
        return this.#transformMatrix;
    }

    set scaleX(value: number) {
        this.#transformMatrix.scaleX = value;
    }

    get scaleX() {
        return this.#transformMatrix.scaleX;
    }

    set scaleY(value: number) {
        this.#transformMatrix.scaleY = value;
    }

    get scaleY() {
        return this.#transformMatrix.scaleY;
    }

    set xTranslation(value: number) {
        this.#transformMatrix.xTranslation = value;
    }

    get xTranslation() {
        return this.#transformMatrix.xTranslation;
    }

    set yTranslation(value: number) {
        this.#transformMatrix.yTranslation = value;
    }

    get yTranslation() {
        return this.#transformMatrix.yTranslation;
    }

    #centerPivotForScaleContainers() {
        const width = this.#sprite.width;
        const height = this.#sprite.height;
        this.#scaleRotateContainer.pivot.set(width / 2, height / 2);
        this.#scaleRotateContainer.position.set(width / 2, height / 2);
    }

    #applyFlip() {
        this.#scaleRotateContainer.scale.x = this.flipHorizontally ? -1 : 1;
        this.#scaleRotateContainer.scale.y = this.flipVertically ? -1 : 1;
    }

    #applyRotation() {
        this.#scaleRotateContainer.rotation = this.transformation.rotation;
    }

    /**
     * The rectangle the sprite is drawn in, in reference-resolution pixels, before a mask clips it
     * and before rotation. Falls back to the asset's own box until the animation data has loaded.
     */
    get unclippedBounds(): PIXI.Rectangle {
        const { width, height } = this.#animationData
            ? this.#getSpriteSize()
            : { width: this.maxWidth, height: this.maxHeight };
        const { x, y } = getPositionFromTransformation(
            this.getFreelyPositionedParent().transformation,
            width,
            height,
        );
        return new PIXI.Rectangle(x, y, width, height);
    }

    #applyPosition(offsetX = 0, offsetY = 0) {
        const width = this.#sprite.width;
        const height = this.#sprite.height;
        const anchorX = this.transformation.anchor.x * width;
        const anchorY = this.transformation.anchor.y * height;
        const centerX = width / 2;
        const centerY = height / 2;
        const dx = anchorX - centerX;
        const dy = anchorY - centerY;
        const rotation = this.transformation.rotation;
        const cosR = Math.cos(rotation);
        const sinR = Math.sin(rotation);
        const rotOffsetX = dx * (1 - cosR) + dy * sinR;
        const rotOffsetY = dy * (1 - cosR) - dx * sinR;
        super.applyTransformation(offsetX + rotOffsetX, offsetY + rotOffsetY, -rotation);
        this.#centerPivotForScaleContainers();
    }

    override applyTransformation(offsetX = 0, offsetY = 0) {
        this.#applyPosition(offsetX, offsetY);
        this.#applyFlip();
        this.#applyRotation();
        this._pixiContainer.alpha = this.opacity;
    }

    override resetTransformation() {
        super.resetTransformation();
        this.#scaleRotateContainer.rotation = 0;
        this.#scaleRotateContainer.scale.set(1, 1);
    }

    async getBase64Data(): Promise<{ base64: string; width: number; height: number }> {
        const previousResolution = this.root!.renderer!.resolution;
        this.root!.renderer!.resolution = 1;
        this.updateForRender();
        const { width, height } = this.getVisibleResolution();
        const wasRenderGroup = this.#sprite.isRenderGroup;
        const base64 = await this.root!.renderer!.extract.base64({
            target: this.#sprite,
            format: 'png',
            quality: 1,
        });
        if (this.destroyed) {
            throw new Error('lottie asset is destroyed');
        }
        if (!wasRenderGroup) {
            this.#sprite.disableRenderGroup();
        }

        this.root!.renderer!.resolution = previousResolution;
        this._reusableTextures.detachTextures();
        return { base64, width, height };
    }

    resetEditableTransformations() {
        this.flipHorizontally = false;
        this.flipVertically = false;
        this.transformation.rotation = 0;
        this.scaleX = 1;
        this.scaleY = 1;
        this.xTranslation = 0;
        this.yTranslation = 0;
    }

    override get propertiesToSerialize() {
        /**
         * @todo remove colorMaps once it is made into an array attribute in https://lumen5.atlassian.net/browse/LU-1943,
         */
        return super.propertiesToSerialize
            .concat(getAttributesToSerialize(this))
            .concat('sourceUrl', 'colorMaps', 'mask', 'transformMatrix');
    }
}
